/**
 * Regras da senha — as mesmas na interface (lista de requisitos e medidor de força ao
 * criar, redefinir ou alterar) e no servidor (que recusa a senha que não as cumpre).
 */

export const PASSWORD_MIN_LENGTH = 8;

export interface PasswordRule {
  label: string;
  ok: boolean;
}

/** Requisitos obrigatórios, na ordem da lista exibida. */
export function passwordRules(password: string): PasswordRule[] {
  return [
    { label: `Pelo menos ${PASSWORD_MIN_LENGTH} caracteres`, ok: password.length >= PASSWORD_MIN_LENGTH },
    { label: 'Letras e números', ok: /\p{L}/u.test(password) && /\d/.test(password) },
  ];
}

/** Motivo da recusa (o primeiro requisito não cumprido) ou `null`. */
export function passwordError(password: string): string | null {
  const missing = passwordRules(password).find((r) => !r.ok);

  return missing ? `A senha precisa ter: ${missing.label.toLowerCase()}.` : null;
}

export type PasswordStrength = 0 | 1 | 2 | 3 | 4;

export const STRENGTH_LABEL: Record<Exclude<PasswordStrength, 0>, string> = {
  1: 'Fraca',
  2: 'Razoável',
  3: 'Boa',
  4: 'Forte',
};

/**
 * Força de 0 (vazia) a 4, um ponto por: tamanho mínimo, letras e números, 12 caracteres
 * ou mais, e maiúsculas com minúsculas ou símbolo. A senha não vazia vale pelo menos 1.
 */
export function passwordStrength(password: string): PasswordStrength {
  if (!password) return 0;

  const points = [
    password.length >= PASSWORD_MIN_LENGTH,
    /\p{L}/u.test(password) && /\d/.test(password),
    password.length >= 12,
    (/\p{Lu}/u.test(password) && /\p{Ll}/u.test(password)) || /[^\p{L}\d]/u.test(password),
  ].filter(Boolean).length;

  return Math.max(1, points) as PasswordStrength;
}
